/**
 * The rel="alternate" hreflang set for the page currently being rendered.
 *
 * Sitecore supplies the finished set on the OpenGraph field, built from the same
 * sitemap/site/homeLocales config node the XML sitemap generates its <xhtml:link> elements from.
 * That is deliberate: Google discards an hreflang set whose members do not all reference each
 * other, so a second copy of the locale table in this repo could break both the head and the
 * sitemap silently, by disagreeing with it. This function therefore knows nothing about locales
 * and invents no URLs - it only guards the render.
 *
 * The field is absent on every page but the home item, which is where the scope decision lives:
 * deep pages canonicalise onto their unprefixed URL, so advertising /us/... and /ca/... variants
 * of them would promote three URLs for one page. See lib/canonical.js for the matching reasoning.
 *
 * @param {Array<{hreflang: string, href: string}>} alternates from Sitecore, may be undefined
 * @returns {Array<{hreflang: string, href: string}>} safe to map over; empty when there is none
 */
export const resolveHreflangLinks = (alternates) => {
  if (!Array.isArray(alternates)) {
    return [];
  }

  const seen = new Set();

  return alternates.filter((link) => {
    if (!link || !link.hreflang || !link.href) {
      return false;
    }

    // A repeated hreflang invalidates the whole set, so drop the later one rather than
    // emit a conflict. It also keeps the React key below unique.
    if (seen.has(link.hreflang)) {
      return false;
    }

    seen.add(link.hreflang);

    return true;
  });
};
